import {AsyncPipe} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {BehaviorSubject, filter, Observable, Subscription} from 'rxjs';
import {take, withLatestFrom} from 'rxjs/operators';
import {Project, ProjectView, ProjectViewToken, RepeatingTaskHistoryItem} from 'src/app/core/model/project';
import {CurrentSkedTask} from 'src/app/core/model/task';
import {Tracker} from 'src/app/core/model/tracker';
import {User} from 'src/app/core/model/user';
import {CurrentSkedChangeTaskStatusAction} from 'src/app/core/store/current-sked/current-sked.action';
import {
  GetProjectViewAction,
  GetProjectViewRepeatingTaskHistoryAction,
  UpdateProjectViewTaskId,
  UpdateRepeatingTaskHistoryMapAction,
  UpdateRoutineDetailParamsAction,
  UpdateScrollPositionAction,
} from 'src/app/core/store/projects/projects.action';
import {
  selectProjectViewRoutineDetailParams,
  selectProjectViews,
  selectProjectViewScrollPosition,
  selectProjectViewTokenFilterParams,
  selectRepeatingTaskHistoryMap,
} from 'src/app/core/store/projects/projects.selector';
import {CreateNewTrackerAction, UpdateTempTrackerProjectIdAction} from 'src/app/core/store/trackers/trackers.action';
import {selectUsersMap} from 'src/app/core/store/users/users.selector';
import {TaskStatusActions} from 'src/app/current-sked/shared/task-status-actions';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {RolePermissionOptions} from './../../core/model/role';
import {ProjectViewActivityItemComponent} from './activity-item/project-view-activity-item.component';
import {ProjectViewItemHeaderComponent} from './item-header/project-view-item-header.component';
import {ProjectViewLineItemComponent} from './line-item/project-view-line-item.component';
import {ProjectViewActivityIconPipe} from './pipes/project-view-activity-icon.pipe';
import {ProjectViewProgressComponent} from './progress/project-view-progress.component';
import {ProjectViewTaskComponent} from './task/project-view-task.component';
import {ProjectViewTrackerComponent} from './tracker/project-view-tracker.component';

interface FormattedTracker {
  mostRecentlyUpdatedMetricIndex: number;
  tracker: Tracker;
}

@Component({
  selector: 'project-view',
  standalone: true,
  imports: [
    ProjectViewItemHeaderComponent,
    ProjectViewProgressComponent,
    AsyncPipe,
    ProjectViewLineItemComponent,
    ProjectViewTaskComponent,
    ProjectViewActivityItemComponent,
    ProjectViewActivityIconPipe,
    ProjectViewTrackerComponent,
    OphIconModule,
    MatTooltipModule,
    PipesModule,
    OphLoadingModule,
  ],
  templateUrl: './project-view.component.html',
  styleUrl: './project-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectViewComponent implements OnInit, OnDestroy, AfterViewInit {
  /** When provided (e.g. from wrapper), this is the project to show. When not provided, component may not load data. */
  project = input<Project>();
  projectSelected = output<Project>();

  /** Selected project from parent input (as observable for subscriptions). */
  selectedProject$ = toObservable(this.project);

  // Store selectors
  usersMap$: Observable<Record<string, User>> = this.store$.pipe(select(selectUsersMap));
  tokenParams$: Observable<Record<string, string>> = this.store$.pipe(select(selectProjectViewTokenFilterParams));

  subscriptions = new Subscription();
  repeatingTaskHistoryMap$: Observable<Record<string, RepeatingTaskHistoryItem>> = this.store$.pipe(
    select(selectRepeatingTaskHistoryMap)
  );
  routineDetailParams$ = this.store$.pipe(select(selectProjectViewRoutineDetailParams));
  projectViews$: Observable<Record<string, ProjectView>> = this.store$.pipe(select(selectProjectViews));

  projectView$ = new BehaviorSubject<ProjectView | null>(null);
  timeLeft$ = new BehaviorSubject<{percentage: number; display: string}>(null);
  progressText$ = new BehaviorSubject<string>('');
  formattedTrackers$ = new BehaviorSubject<FormattedTracker[]>(null);
  addNewButtonLoading$ = new BehaviorSubject<boolean>(false);
  displayTokens$ = new BehaviorSubject<ProjectViewToken[]>([]);
  tasks$ = new BehaviorSubject<CurrentSkedTask[]>([]);
  loading$ = new BehaviorSubject<boolean>(true);

  rolePermissionOptions = RolePermissionOptions;

  readonly messageService = inject(MessageService);

  constructor(
    private timezoneService: TimezoneService,
    private store$: Store,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.subscriptions.add(this.subscribeToSelectedProject());
    this.subscriptions.add(this.subscribeToProjectView());
  }

  ngAfterViewInit() {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.setScrollPosition();
    });
  }

  ngOnDestroy() {
    this.store$.dispatch(new UpdateProjectViewTaskId({id: null}));
    this.subscriptions.unsubscribe();
  }

  private setScrollPosition() {
    this.store$
      .pipe(select(selectProjectViewScrollPosition))
      .pipe(take(1))
      .subscribe(position => {
        if (position > 0) {
          // Set scroll position immediately - the container should be available by now
          const scrollContainer = document.querySelector('.oph-feature-layout');
          if (scrollContainer) {
            scrollContainer.scrollTop = position;
            // Force a re-render to ensure the scroll position is applied
            this.cdr.detectChanges();
          }
        }
      });
  }

  subscribeToSelectedProject(): Subscription {
    return this.selectedProject$
      .pipe(
        filter((p): p is Project => !!p),
        withLatestFrom(this.projectViews$)
      )
      .subscribe(([project, projectViews]) => {
        if (!projectViews[project._id]) {
          this.loading$.next(true);
        } else {
          this.projectView$.next(projectViews[project._id]);
          this.loading$.next(false);
        }
        this.getProjectView(project._id);
      });
  }

  getProjectView = (projectId: string) => {
    this.tokenParams$.pipe(take(1)).subscribe(params => {
      this.store$.dispatch(
        new GetProjectViewAction({
          projectId,
          params,
          onSuccess: projectView => this.onGetProjectViewSuccess(projectView),
          onFailure: err => this.onGetProjectViewFailure(err),
        })
      );
    });
  };

  onGetProjectViewSuccess(projectView: ProjectView) {
    this.projectView$.next(projectView);
    this.loading$.next(false);
  }

  onGetProjectViewFailure(err: Error) {
    this.messageService.add(err.message || 'Error loading project view');
  }

  subscribeToProjectView(): Subscription {
    return this.projectView$.subscribe(project => {
      if (project) {
        this.initialize(project);
      }
    });
  }

  initialize(project: ProjectView) {
    this.timeLeft$.next(this.getTimeLeft(project));
    this.progressText$.next(this.getProgressText(project));
    this.formattedTrackers$.next(this.formatTrackers(project));
    this.displayTokens$.next(this.getDisplayTokens(project));
    this.tasks$.next(this.getTasks(project));
  }

  getTasks(project: ProjectView): CurrentSkedTask[] {
    // This flattens out the data being sent from backend
    const expiredTasksWithFlag =
      project.expiredTasks?.map(task => ({
        ...task,
        expired: true,
      })) || [];

    const allTasks = [
      ...(project.completedTasks || []),
      ...expiredTasksWithFlag,
      ...(project.inProgressTasks || []),
      ...(project.onHoldTasks || []),
      ...(project.todoTasks || []),
    ];

    // Filter tasks by batchId - only keep the most recent task per batch
    const batchMap = new Map<string, CurrentSkedTask>();
    const tasksWithoutBatch: CurrentSkedTask[] = [];

    allTasks.forEach(task => {
      if (task.batchId) {
        const existingTask = batchMap.get(task.batchId);
        if (!existingTask || moment(task.startDateTime).isAfter(moment(existingTask.startDateTime))) {
          batchMap.set(task.batchId, task);
        }
      } else {
        tasksWithoutBatch.push(task);
      }
    });

    return [...Array.from(batchMap.values()), ...tasksWithoutBatch];
  }

  getDisplayTokens(project: ProjectView): ProjectViewToken[] {
    return project.tokens?.filter(t => t.token?.name && t.token?.icon);
  }

  formatTrackers(project: ProjectView): FormattedTracker[] {
    if (!project.trackers?.length) {
      return [];
    }

    return project.trackers.map(tracker => {
      // Find the most recently updated metric for display purposes
      const mostRecentlyUpdatedMetricIndex = tracker.metrics.reduce((latestIndex, metric, index, metrics) => {
        const currentMoment = moment(metric.lastUpdatedAt);
        const latestMoment = moment(metrics[latestIndex].lastUpdatedAt);
        return currentMoment.isAfter(latestMoment) ? index : latestIndex;
      }, 0);

      return {
        mostRecentlyUpdatedMetricIndex,
        tracker,
      };
    });
  }

  getTimeLeft(project: ProjectView): {percentage: number; display: string} {
    if (!project.endDateTime) {
      return {percentage: 100, display: 'Never expires'};
    }

    if (project.status === 'inactive') {
      return {percentage: 0, display: 'Project is in draft mode'};
    }
    const timezone = this.timezoneService.getTimeZone();
    const startTime = moment.tz(project.startDateTime, timezone);
    const endTime = moment.tz(project.endDateTime, timezone);
    const currentTime = moment.tz(timezone);

    const totalDuration = moment.duration(endTime.diff(startTime));
    const elapsedTime = moment.duration(currentTime.diff(startTime));
    const remainingTime = moment.duration(endTime.diff(currentTime));

    let percentage = 100 - Math.round((elapsedTime.asMilliseconds() / totalDuration.asMilliseconds()) * 100);
    if (percentage < 0) {
      percentage = 0;
    }
    if (percentage > 100) {
      percentage = 100;
    }
    const expiredDisplay = percentage === 0 ? 'Expired' : 'Expires';
    const display = `${expiredDisplay} ${remainingTime.humanize(true)}`;

    return {percentage, display};
  }

  getProgressText(project: ProjectView): string {
    if (!project.progress?.tasksRemaining) {
      return 'This project has no tasks';
    }

    if (project.progress.tasksRemaining === 1) {
      return '1 Task remains';
    } else {
      return `${project.progress.tasksRemaining} Tasks remain`;
    }
  }

  onTaskClick(event: {task: CurrentSkedTask; isRepeating: boolean}) {
    const scrollTop = this.getScrollPosition();

    if (this.isRepeatingTask(event.task)) {
      this.handleRepeatingTaskClick(event, scrollTop);
    } else {
      this.navigateToTask(event.task._id, scrollTop);
    }
  }

  private isRepeatingTask(task: CurrentSkedTask): boolean {
    return !!task.batchId;
  }

  private handleRepeatingTaskClick(event: {task: CurrentSkedTask; isRepeating: boolean}, scrollTop: number): void {
    this.repeatingTaskHistoryMap$.pipe(take(1)).subscribe(historyMap => {
      if (event.isRepeating) {
        this.navigateToTask(event.task._id, scrollTop);
        return;
      }

      this.toggleRepeatingTaskExpansion(event.task, historyMap);
    });
  }

  private toggleRepeatingTaskExpansion(
    task: CurrentSkedTask,
    historyMap: Record<string, RepeatingTaskHistoryItem>
  ): void {
    const existingItem = historyMap[task.taskInstanceId];
    const isNewItem = !existingItem;
    const expanded = isNewItem ? false : !existingItem.expanded;

    const repeatingTaskHistoryItem: RepeatingTaskHistoryItem = {
      id: task.taskInstanceId,
      tasks: existingItem?.tasks ? [...existingItem.tasks] : [],
      expanded,
      loading: isNewItem || expanded,
      pagination: existingItem?.pagination || null,
    };

    this.store$.dispatch(new UpdateRepeatingTaskHistoryMapAction(repeatingTaskHistoryItem));

    if (isNewItem || expanded) {
      this.getRoutineDetail(task.taskInstanceId, repeatingTaskHistoryItem);
    }
  }

  private navigateToTask(taskId: string, scrollTop: number): void {
    this.store$.dispatch(new UpdateScrollPositionAction({scrollPosition: scrollTop}));
    // this.router.navigate([this.project()._id, 'task', taskId], {state: {scrollPosition: scrollTop}}); // Navigate to
    this.router.navigate(['task', taskId], {relativeTo: this.route});
  }

  private getScrollPosition(): number {
    const scrollContainer = document.querySelector('.oph-feature-layout');
    if (scrollContainer) {
      const scrollTop = scrollContainer.scrollTop;
      return scrollTop || 0;
    }
  }

  onAddTracker() {
    this.addNewButtonLoading$.next(true);
    this.store$.dispatch(
      new CreateNewTrackerAction({
        onSuccess: tracker => this.onCreateTrackerSuccess(tracker),
        onFailure: err => this.onCreateTrackerFailure(err),
      })
    );
  }

  onCreateTrackerSuccess(tracker: Tracker) {
    if (tracker?._id) {
      const project = this.project();
      if (project) {
        this.store$.dispatch(new UpdateTempTrackerProjectIdAction({projectId: project._id}));
        this.router.navigate(['trackers', tracker._id, {returnTo: this.router.url}]);
      }
    }
    this.addNewButtonLoading$.next(false);
  }

  onCreateTrackerFailure(err: Error) {
    this.addNewButtonLoading$.next(false);
    this.messageService.add(err.message || 'There was a problem creating the tracker');
  }

  getRoutineDetail(taskInstanceId: string, repeatingTaskHistoryItem?: RepeatingTaskHistoryItem) {
    this.routineDetailParams$.pipe(take(1)).subscribe(params => {
      this.store$.dispatch(
        new GetProjectViewRepeatingTaskHistoryAction({
          taskInstanceId,
          params,
          onFailure: err => this.onGetRoutineDetailFailure(err),
        })
      );
    });
  }

  onGetRoutineDetailFailure(err: Error) {
    this.messageService.add(err.message || 'There was a problem getting routine details.');
  }

  onTokenTimeFilterChange() {
    const project = this.project();
    if (project) {
      this.getProjectView(project._id);
    }
  }

  onPageChange(page: number, taskInstanceId: string) {
    this.routineDetailParams$.pipe(take(1)).subscribe(currentParams => {
      const params = {...currentParams, page};
      this.store$.dispatch(new UpdateRoutineDetailParamsAction({params}));
      this.getRoutineDetail(taskInstanceId);
    });
  }

  onCheckbox(event: {checked: boolean; task: CurrentSkedTask}) {
    const actionType = event.checked ? TaskStatusActions.Complete : TaskStatusActions.Release;
    this.store$.dispatch(
      new CurrentSkedChangeTaskStatusAction({
        id: event.task._id,
        actionType,
        onSuccess: task => {
          this.onActionSuccess(event.task.taskInstanceId);
        },
        onFailure: () => this.onActionFailure(),
      })
    );
  }

  onActionSuccess(taskInstanceId: string) {
    this.getRoutineDetail(taskInstanceId);
  }

  onActionFailure() {
    this.messageService.add(`There was a problem changing the task status.`);
  }

  onBackClick() {
    this.router.navigate(['/projects']);
  }
}
